import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  TextInput,
  Switch,
  Group,
  Title,
  Tabs,
  Stack,
  ActionIcon,
  Text,
  Table,
  Select,
  Autocomplete,
  ScrollArea
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
} from 'mantine-react-table';
import { PhoneInput, PhoneInputValue } from "../components/PhoneInput.tsx";
import { FileUploadByImage } from "./UsersPage.tsx";
import Environment from "../utility/environment.ts";
import { useAuth } from "../contexts/AuthProvider.tsx";
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { enableGlobalSearchOnColumns, withMrtContainsSearch } from '../utility/mrtTableSearch';
import { useModalScrollAreaHeight } from '../utility/mobilePageLayout';

// Tipagens locais

type Enterprise = {
  id: number;
  uuid: string;
  razaoSocial: string;
  tax_id: string;
  email: string;
  phone_number: string;
  status: boolean;
};

type EnterpriseForm = Omit<Enterprise, 'id' | 'uuid'>;

const initialEnterpriseForm: EnterpriseForm = {
  razaoSocial: '',
  tax_id: '',
  email: '',
  phone_number: '',
  status: true,
};

type User = {
  id: number;
  uuid: string;
  enterprise_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  cargo: string;
  username: string;
  photo: string | File;
  status: string;
};

const initialForm: UserForm = {
  first_name: '',
  last_name: '',
  email: '',
  role: 'user',
  cargo: '',
  username: '',
  photo: 'https://cdn-icons-png.flaticon.com/512/149/149452.png',
  phone: '',
  status: 'active',
};

type UserForm = Omit<User, 'id' | 'uuid' | 'enterprise_id'>;

export default function EnterprisePage() {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const [users, setUsers] = useState<User[]>([]);
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [modalMode, setModalMode] = useState<'manual' | 'automático'>('manual');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<UserForm>(initialForm);
  const nameLength = 50;
  const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  const usernameRegex = /^[a-zA-Z0-9_.-]+$/;
  const [usernameError, setUsernameError] = useState<string | undefined>(undefined);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);


  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [editingEnterprise, setEditingEnterprise] = useState<Enterprise | null>(null);
  const [enterpriseForm, setEnterpriseForm] = useState<EnterpriseForm>(initialEnterpriseForm);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingEnterprise, setIsEditingEnterprise] = useState(false);


  const [usersModalOpened, setUsersModalOpened] = useState(false);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [linkedUsers, setLinkedUsers] = useState<User[]>([]);
  const [unlinkedUsers, setUnLinkedUsers] = useState<User[]>([]);
  const [createdUsers, setCreatedUsers] = useState<UserForm[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<User[]>([]);


  const OpenUserModal = (user?: User) => {
    setUserForm(user ? user : initialForm);
    setEditingUser(user ? user : null);
    setIsEditingUser(user ? true : false);
    setUsersModalOpened(true);
  };


  const openModal = (enterprise?: Enterprise) => {
    setLinkedUsers([]);
    setUnLinkedUsers([]);
    if (enterprise) {
      console.log('Editing enterprise:', enterprise);
      setEnterpriseForm({
        razaoSocial: enterprise.razaoSocial,
        tax_id: enterprise.tax_id,
        email: enterprise.email,
        phone_number: enterprise.phone_number,
        status: enterprise.status,
      });
      setEditingEnterprise(enterprise);
      setIsEditingEnterprise(true);

      getUsers(enterprise.uuid); // Atualiza a lista de usuários ao abrir o modal

    } else {
      setEnterpriseForm(initialEnterpriseForm);
      setEditingEnterprise(null);
      setIsEditingEnterprise(false);
      setEnterpriseUsers([]);
    }
    setModalOpened(true);
  };

  const isFormValid = () => {
    return (
      enterpriseForm.razaoSocial.trim() !== '' &&
      enterpriseForm.tax_id.trim() !== '' &&
      enterpriseForm.email.trim() !== ''
    );
  };

  const isUserFormValid = () => {
    return (
      userForm.first_name.trim() !== '' &&
      userForm.last_name.trim() !== '' &&
      userForm.email.trim() !== '' &&
      userForm.role.trim() !== '' &&
      userForm.cargo.trim() !== '' &&
      userForm.username.trim() !== '' &&
      userForm.username.length >= 6 &&
      emailRegex.test(userForm.email) &&
      usernameRegex.test(userForm.username)
    );
  };

  const getEnterprises = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/enterprises/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedProducts: Enterprise[] = data.map((product: any, index: number) => ({
          id: index + 1,
          uuid: product.id,
          razaoSocial: product.legal_name,
          tax_id: product.tax_id,
          email: product.email,
          phone_number: product.phone_number,
          status: product.status,
        }));

        setEnterprises(formattedProducts);
      })
      .catch((err) => console.error('Erro ao buscar Produtos:', err));
  };

  const linkedUsersToEnterprise = (user_id: string, enterprise_id: string) => {
    fetch(`${Environment.VITE_API_URL}/api/v1/link_user/${user_id}/${enterprise_id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(linkedUsers),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // setUpdateNotifications(!updateNotifications);
        getEnterprises();
      });
  };
  const unlinkedUsersToEnterprise = (user_id: string, enterprise_id: string) => {
    fetch(`${Environment.VITE_API_URL}/api/v1/link_user/${user_id}/${enterprise_id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(linkedUsers),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // setUpdateNotifications(!updateNotifications);
        getEnterprises();
      });
  }


  const saveEnterprise = () => {
    if (!isFormValid()) return;

    fetch(`${Environment.VITE_API_URL}/api/v1/enterprises`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatEnterpriseForm(enterpriseForm)),
    })
      .then((res) => res.json())
      .then(async (data) => {
        console.log(data);
        if (createdUsers.length > 0) {
          console.log(data);
          if (createdUsers.length > 0) {
            await Promise.all(createdUsers.map(user => createUser(user, data.id)));
          }
        }
        if (deletedUsers.length > 0) {
          deletedUsers.forEach((user) => {
            deleteUser(user);
          });
        }
        if (linkedUsers.length > 0) {
          linkedUsers.forEach((user) => {
            linkedUsersToEnterprise(user.uuid, data.id);
          });
        }
        if (unlinkedUsers.length > 0) {
          unlinkedUsers.forEach((user) => {
            unlinkedUsersToEnterprise(user.uuid, data.id);
          });
        }
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // setUpdateNotifications(!updateNotifications);
        setCreatedUsers([]);
        setDeletedUsers([]);
        setLinkedUsers([]);
        setUnLinkedUsers([]);
        getEnterprises();
      });
  };

  const updateEnterprise = () => {
    if (!isFormValid()) return;

    fetch(`${Environment.VITE_API_URL}/api/v1/enterprises/${editingEnterprise?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatEnterpriseForm(enterpriseForm)),
    })
      .then((res) => res.json())
      .then(async (data) => {
        console.log(data);

        if (createdUsers.length > 0) {
          console.log(data);
          if (createdUsers.length > 0) {
            await Promise.all(createdUsers.map(user => createUser(user, data.id)));
          }
        }
        if (deletedUsers.length > 0) {
          deletedUsers.forEach((user) => {
            deleteUser(user);
          });
        }
        if (linkedUsers.length > 0) {
          linkedUsers.forEach((user) => {
            console.log('Linking user:', user, 'to enterprise:', data.id);
            linkedUsersToEnterprise(user.uuid, data.id);
          });
        }
        if (unlinkedUsers.length > 0) {
          unlinkedUsers.forEach((user) => {
            console.log('Unlinking user:', user, 'from enterprise:', data.id);
            unlinkedUsersToEnterprise(user.uuid, data.id);
          });
        }
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // setUpdateNotifications(!updateNotifications);
        setCreatedUsers([]);
        setDeletedUsers([]);
        setLinkedUsers([]);
        setUnLinkedUsers([]);
        getEnterprises();
      });
  }

  // const deleteEnterprise = () => {
  //   if (!editingEnterprise) return;
  //   setEnterprises((prev) => prev.filter((e) => e.id !== editingEnterprise.id));
  //   setModalOpened(false);
  // };

  const formatEnterpriseForm = (enterprise: EnterpriseForm) => {
    return {
      legal_name: enterprise.razaoSocial,
      tax_id: enterprise.tax_id,
      email: enterprise.email,
      phone_number: enterprise.phone_number,
      status: enterprise.status ? "ACTIVE" : "INACTIVE",
    };
  };

  const handleRowDoubleClick = (row: Enterprise) => {
    openModal(row);
    getUsers(row.uuid); // Atualiza a lista de usuários ao abrir o modal
  };

  const handleRowUserDoubleClick = (row: User) => {
    OpenUserModal(row);
  };

  const localization = useMemo(() => buildMrtLocalization(t), [t, i18n.language]);

  const columns = useMemo<MRT_ColumnDef<Enterprise>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'razaoSocial',
        header: t('enterprises.columns.companyName'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'tax_id',
        header: t('enterprises.columns.taxId'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'email',
        header: t('enterprises.columns.email'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'phone_number',
        header: t('enterprises.columns.phoneNumber'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'status',
        header: t('enterprises.columns.status'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('enterprises.form.statusActive') : t('enterprises.form.statusInactive')),
      },
    ],
      ['razaoSocial', 'tax_id', 'email'],
    ),
    [t, i18n.language],
  );


  const sendPhoto = async (user: UserForm): Promise<string | null> => {
    const file = user.photo;

    if (file instanceof File) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          return data.image_url; // Retorna apenas a URL da imagem
        }
      } catch (error) {
        console.error('Erro ao enviar imagem', error);
      }
    }

    return null;
  };


  const getUsers = (enterprise_id: string) => {
    fetch(`${Environment.VITE_API_URL}/api/v1/users/${enterprise_id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedUsers: User[] = data.map((user: any, index: number) => ({
          id: index + 1,
          uuid: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role,
          cargo: user.cargo,
          username: user.username,
          photo: user.photo,
          phone: user.phone_number,
          status: user.status,
        }));
        setEnterpriseUsers(formattedUsers);
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err));
  };

  const deleteUser = (user: User) => {
    fetch(`${Environment.VITE_API_URL}/api/v1/users/${user?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          setNotification({ message: 'O usuario nao pode ser deletada, pois está vinculado a alguma oportunidade ou atividade', color: 'red' });
          setErrorMessage('O usuario nao pode ser deletada, pois está vinculado a alguma oportunidade ou atividade');
        }
        console.log(response);
        if (response.status == 200) {
          setUsersModalOpened(false);
          // getUsers();
        }
      })
      .catch(() => {
        // console.error(error);
        setUsersModalOpened(true);
      });
  };

  const formatUserForCreation = (contact: UserForm) => {
    return {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      role: contact.role,
      cargo: contact.cargo,
      username: contact.username,
      photo: contact.photo,
      status: contact.status,
      phone_number: contact.phone
    };
  };

  const updateUser = async (user: User) => {
    const uploadedPhotoUrl = await sendPhoto(user);
    var userPayload

    if (uploadedPhotoUrl) {
      userPayload = {
        ...user,
        photo: uploadedPhotoUrl, // Aqui garantimos que é string
      }
    } else {
      userPayload = {
        ...user,
      }
    }

    fetch(`${Environment.VITE_API_URL}/api/v1/users/${editingUser?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatUserForCreation(userPayload)),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setUsersModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (editingEnterprise) {
          getUsers(editingEnterprise.uuid);
        }
      });
  };

  const addCreatedUser = (user: UserForm) => {
    const newUser: User = {
      ...user,
      id: 0, // or generate/assign a real id
      uuid: '', // or generate/assign a real uuid
      enterprise_id: '', // or assign as needed
    }
    setEnterpriseUsers((prevUsers) => [...prevUsers, newUser]);
    setCreatedUsers((prevCreated) => [...prevCreated, user]);
    setUsersModalOpened(false);
  };

  const addDeletedUser = (user: User) => {
    setEnterpriseUsers((prevUsers) => prevUsers.filter((u) => u.uuid !== user.uuid));
    if (user.id) {
      setDeletedUsers((prevDeleted) => [...prevDeleted, user]);
    }
    setUsersModalOpened(false);
  };

  const createUser = async (user: UserForm, enterprise_id?: string) => {
    const uploadedPhotoUrl = await sendPhoto(user);
    var userPayload

    if (uploadedPhotoUrl) {
      userPayload = {
        ...user,
        photo: uploadedPhotoUrl, // Aqui garantimos que é string
      }
    } else {
      userPayload = {
        ...user,
      }

      fetch(`${Environment.VITE_API_URL}/api/v1/users/${enterprise_id ? enterprise_id : editingEnterprise?.uuid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatUserForCreation(userPayload)),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log(data);
          setUsersModalOpened(false);
        })
        .catch((error) => {
          console.error(error);
          setNotification({ message: 'Erro ao criar o usuário', color: 'red' });
          setErrorMessage('Erro ao criar o usuário: Email ou Username em uso');
        })
        .finally(() => {
          if (editingEnterprise) {
            getUsers(editingEnterprise.uuid);
          }
        });
    }
  };

  const validateUsername = async (username: string) => {
    if (!username || username.length < 6 || username.length > 20 || !usernameRegex.test(username)) {
      return; // Falha local, não precisa ir ao backend
    }

    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/validate_username/${username}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        if (data?.detail === "Username already exists") {
          setUsernameError("Username já existe");
          return false
        } else {
          setUsernameError("Erro ao validar username");
          return false
        }
      } else {
        setUsernameError(undefined); // Username válido
        return true
      }
    } catch (error) {
      console.error(error);
      setUsernameError("Erro de conexão com servidor");
      return false
    }
  };

  const validateEmail = async (email: string) => {
    if (!emailRegex.test(email)) {
      return; // Falha local, não precisa ir ao backend
    }

    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/validate_email/${email}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        if (data?.detail === "Email already exists") {
          setEmailError("Email já existe");
          return false
        } else {
          setEmailError("Erro ao validar o email");
          return false
        }
      } else {
        setEmailError(undefined); // Username válido
        return true
      }
    } catch (error) {
      console.error(error);
      setEmailError("Erro de conexão com servidor");
      return false
    }
  };

  const getAllUsers = (): User[] => {
    var users: User[] = [];
    fetch(`${Environment.VITE_API_URL}/api/v1/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Usuarios:', data);
        const formattedUsers: User[] = data.map((user: any, index: number) => ({
          id: index + 1,
          uuid: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role,
          cargo: user.cargo,
          username: user.username,
          photo: user.photo,
          phone: user.phone_number,
          status: user.status,
        }));
        setEnterpriseUsers(formattedUsers);
        setSearchUsers(formattedUsers);
        users = formattedUsers;
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err))
      .finally(() => {
        return users;
      });
    return users;
  };


  const [selectedUserLabel, setSelectedUserLabel] = useState<string>('');

  useEffect(() => {
    document.title = 'aPlus CRM - Enterprises';
    getEnterprises();

    // TODO: Ajustar depois com os usuários reais
    const allUsers = getAllUsers();
    setSearchUsers(allUsers);
    setUsers(allUsers)
  }, [accessToken]);

  useEffect(() => {
    if (editingEnterprise) getUsers(editingEnterprise.uuid)
  }, [editingEnterprise]);

  return (
    <>
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('enterprises.title')}</Title>
        <Button onClick={() => openModal()}>{t('enterprises.addEnterprise')}</Button>
      </Group>

      <MantineReactTable
        {...withMrtContainsSearch({
        columns,
        data: enterprises,
        localization,
        defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
        enableStickyHeader: true,
        enableColumnFilters: true,
        enableSorting: true,
        enableFullScreenToggle: true,
        enableGrouping: false,
        enableColumnActions: false,
        enablePagination: true,
        enableBottomToolbar: true,
        enableRowVirtualization: false,
        initialState: { showGlobalFilter: true },
        mantineTableProps: {
          sx: {
            display: 'table',
            tableLayout: 'auto',
            borderCollapse: 'separate',
            '& .mantine-TableHeadCell-Content': {
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1px 1px',
            },
            '& .mantine-TextInput-input': {
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#000',
            },
            '& th, & td': {
              borderRadius: '4px',
            },
            '& thead': {
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'rgba(48, 113, 91, 0.9)',
              color: '#fff',
            },
          },
        },
        mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Enterprise> }) => ({
          onDoubleClick: () => handleRowDoubleClick(row.original),
          style: { cursor: 'pointer' },
        }),
        })}
      />

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={isEditingEnterprise ? t('enterprises.editEnterprise') : t('enterprises.addNewEnterprise')}
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <Tabs value="manual">
          <Tabs.List>
            <Tabs.Tab value="manual">{t('common.import.manual')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="manual" pt="xs">
            <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                <Stack>
                  <TextInput
                    label={t('enterprises.form.companyName')}
                    value={enterpriseForm.razaoSocial}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, razaoSocial: e.target.value })}
                    required
                  />
                  <TextInput
                    label={t('enterprises.form.taxId')}
                    value={enterpriseForm.tax_id}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, tax_id: e.target.value })}
                    required
                  />
                  <TextInput
                    label={t('enterprises.form.email')}
                    value={enterpriseForm.email}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, email: e.target.value })}
                    required
                  />
                  <PhoneInput
                    value={{
                      country: enterpriseForm.phone_number.split(' ')[0] || '+55',
                      phone: enterpriseForm.phone_number.split(' ').slice(1).join(' ') || '',
                    }}
                    onChange={(value: PhoneInputValue) =>
                      setEnterpriseForm({
                        ...enterpriseForm,
                        phone_number: `${value.country} ${value.phone}`.trim(),
                      })
                    }
                  />

                  <Stack spacing="xs" mb="sm">
                    <Text weight={600} size="lg">
                      {t('enterprises.form.users')}
                    </Text>

                    <Button
                      onClick={() => { OpenUserModal() }}
                      variant="outline"
                      leftIcon={<IconPlus size={16} />}
                      style={{ width: 'fit-content' }}
                    >
                      {t('enterprises.form.createNewUser')}
                    </Button>

                    <Group grow noWrap spacing="xs" align="flex-end">
                      <Autocomplete
                        label={t('enterprises.form.searchLinkUsers')}
                        placeholder={t('enterprises.form.searchPlaceholder')}
                        data={searchUsers.sort((userA, userB) => userA.first_name.localeCompare(userB.first_name)).map((user) => {
                          const label = `${user.first_name} ${user.last_name} - ${user.email}`;
                          return { value: label, label };
                        })}
                        value={selectedUserLabel}
                        onChange={(value) => setSelectedUserLabel(value)}
                        nothingFound={t('enterprises.form.noUsersFound')}
                        style={{ flexGrow: 1 }}
                        maxDropdownHeight={150}
                        limit={searchUsers.length}
                      />

                      <Button
                        style={{ flexGrow: 0 }}
                        disabled={!selectedUserLabel}
                        onClick={() => {
                          const selected = searchUsers.find((user) => {
                            const label = `${user.first_name} ${user.last_name} - ${user.email}`;
                            return label === selectedUserLabel;
                          });
                          if (selected && !enterpriseUsers.some((u) => u.uuid === selected.uuid)) {
                            setEnterpriseUsers([...enterpriseUsers, selected]);
                            setSelectedUserLabel('');
                            console.log('Selected user:', selected);
                            setLinkedUsers((prev) => [...prev, selected]);
                          }

                        }}
                      >
                        {t('enterprises.form.linkUser')}
                      </Button>
                    </Group>
                  </Stack>


                  <Stack spacing="xs" mb="sm">
                    <Table highlightOnHover withBorder striped>
                      <thead>
                        <tr>
                          <th>{t('users.columns.username')}</th>
                          <th>{t('users.columns.firstName')}</th>
                          <th>{t('users.columns.email')}</th>
                          <th>{t('mrt.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enterpriseUsers.map((user, idx) => (
                          <tr
                            key={idx}
                            onDoubleClick={() => handleRowUserDoubleClick(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{user.username}</td>
                            <td>{user.first_name}</td>
                            <td>{user.email}</td>
                            <td>
                              <Group spacing="xs">
                                <ActionIcon
                                  size="md"
                                  variant="filled"
                                  onClick={() => {
                                    setEnterpriseUsers((prev) =>
                                      prev.filter((u) => u.uuid !== user.uuid)
                                    )
                                    setUnLinkedUsers((prev) => [...prev, user])
                                  }
                                  }
                                  sx={{ backgroundColor: 'rgba(48,113,91,0.2)' }}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Stack>

                  <Switch
                    label={t('common.status')}
                    checked={enterpriseForm.status}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, status: e.currentTarget.checked })}
                  />
                </Stack>
              </div>
            </ScrollArea>
            <Group position="right" mt="md" style={{ padding: '1rem' }}>
              <Button onClick={isEditingEnterprise ? updateEnterprise : saveEnterprise} disabled={!isFormValid()}>
                {isEditingEnterprise ? t('common.update') : t('common.save')}
              </Button>
            </Group>
          </Tabs.Panel>
        </Tabs>
      </Modal>

      <Modal
        opened={usersModalOpened}
        onClose={() => setUsersModalOpened(false)}
        centered
        title={
          modalMode === 'manual'
            ? isEditingUser
              ? t('enterprises.form.editUser')
              : t('enterprises.form.addNewUser')
            : t('users.importUsers')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <Text color={notification?.color} align="center">
          {notification?.message}
        </Text>

        <Tabs
          value={modalMode}
          onTabChange={(value: 'manual' | 'automático') => setModalMode(value)}
        >
          {/* Quando estiver editando um cliente, não mostra as abas */}
          {!isEditingEnterprise && (
            <Tabs.List>
              <Tabs.Tab value="manual">{t('common.import.manual')}</Tabs.Tab>
            </Tabs.List>
          )}

          {/* Aba Manual */}
          <Tabs.Panel value="manual" pt="xs">
            {/* -- Campos Obrigatórios -- */}
            <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                <Group
                  spacing="md"
                  mb="sm"
                  align="flex-end"
                  style={{ maxWidth: '100%' }}>
                  <FileUploadByImage user={userForm} setUserForm={setUserForm} />
                  <Group mb="sm" style={{ flexDirection: 'column', flex: 3 }}>
                    <TextInput
                      label={t('users.form.firstName')}
                      placeholder={t('users.form.firstNamePlaceholder')}
                      value={userForm.first_name}
                      onChange={(e) =>
                        setUserForm({ ...userForm, first_name: e.target.value })
                      }
                      mb="sm"
                      required
                      style={{ flex: 1, width: '80%' }}

                    />
                    <Text className={`text-input-counter ${userForm.first_name.length > nameLength ? "error" : ""}`} style={{ width: '80%' }}>
                      {t('settings.characters', { count: userForm.first_name.length, max: nameLength })}
                    </Text>
                    <TextInput
                      label={t('users.form.surname')}
                      placeholder={t('users.form.surnamePlaceholder')}
                      value={userForm.last_name}
                      onChange={(e) =>
                        setUserForm({ ...userForm, last_name: e.target.value })
                      }
                      mb="sm"
                      required
                      style={{ flex: 1, width: '80%' }}
                    />
                    <Text className={`text-input-counter ${userForm.last_name.length > nameLength ? "error" : ""}`} style={{ width: '80%' }}>
                      {t('settings.characters', { count: userForm.last_name.length, max: nameLength })}
                    </Text>
                  </Group>
                </Group>

                <Group grow mb="sm">
                  <Stack>
                    <TextInput
                      label={t('users.form.username')}
                      placeholder={t('users.form.usernamePlaceholder')}
                      value={userForm.username}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUserForm({ ...userForm, username: value });

                        // Limpa o erro enquanto digita
                        setUsernameError(undefined);
                      }}
                      onBlur={() => validateUsername(userForm.username)}
                      mb="sm"
                      required
                      // disabled={isEditing}
                      error={
                        (userForm.username && userForm.username.length < 6
                          ? t('users.validation.usernameMinLength')
                          : undefined) ||
                        (userForm.username && !usernameRegex.test(userForm.username)
                          ? t('users.validation.usernameInvalidFormat')
                          : undefined) ||
                        (userForm.username && userForm.username.length > 20
                          ? t('users.validation.usernameMaxLength')
                          : undefined) ||
                        usernameError
                      }
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${userForm.username.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.username.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>


                <Group grow mb="sm">
                  <Stack>
                    <TextInput
                      label={t('users.form.email')}
                      placeholder={t('users.form.emailPlaceholder')}
                      value={userForm.email}
                      onChange={(e) =>
                        setUserForm({ ...userForm, email: e.target.value })
                      }
                      mb="sm"
                      required
                      error={
                        (userForm.email && !emailRegex.test(userForm.email) ? t('settings.invalidEmailFormat') : undefined) ||
                        emailError
                      }
                      onBlur={() => {
                        if (isEditingUser) {
                          var user = users.find((user) => user.username === userForm.username);
                          if (user && user.email !== userForm.email) {
                            validateEmail(userForm.email)
                          }
                        } else {
                          validateEmail(userForm.email)
                        }
                      }
                      }
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${userForm.email.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.email.length, max: nameLength })}
                    </Text>
                  </Stack>

                  <Stack>
                    <PhoneInput
                      value={{
                        country: userForm.phone.split(' ')[0] || '+55',
                        phone: userForm.phone.split(' ').slice(1).join(' ') || '',
                      }}
                      onChange={(value: PhoneInputValue) =>
                        setUserForm({
                          ...userForm,
                          phone: `${value.country} ${value.phone}`.trim(),
                        })
                      }
                    />
                    <Text
                      className={`text-input-counter ${userForm.phone.length > nameLength ? 'error' : ''
                        }`}
                      style={{ marginTop: -13 /* ajuste em px conforme necessário */ }}
                    >
                      {t('settings.characters', { count: userForm.phone.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>

                <Group grow mb="sm">
                  <Select
                    label={t('users.columns.access')}
                    placeholder={t('common.select')}
                    value={userForm.role}
                    onChange={(value) =>
                      setUserForm({
                        ...userForm,
                        role: value as
                          | 'user'
                          | 'admin'
                      })
                    }
                    data={[
                      { value: 'user', label: t('users.form.accessUser') },
                      { value: 'admin', label: t('users.form.accessAdmin') },
                    ]}
                    mb="sm"
                    required
                    style={{ paddingBottom: 10 }}
                  />
                  <Stack>
                    <TextInput
                      label={t('users.form.role')}
                      placeholder={t('users.form.rolePlaceholder')}
                      value={userForm.cargo}
                      onChange={(e) =>
                        setUserForm({ ...userForm, cargo: e.target.value })
                      }
                      mb="sm"
                      required
                    />
                    <Text className={`text-input-counter ${userForm.cargo.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.cargo.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>


                <Switch
                  label={t('common.status')}
                  checked={userForm.status === 'active'}
                  onChange={(e) =>
                    setUserForm({ ...userForm, status: e.currentTarget.checked ? 'active' : 'inactive' })
                  }
                  mb="sm"
                />
              </div>
            </ScrollArea>
            <Group position="right" mt="md" style={{ padding: '1rem' }}>
              {isEditingUser && (
                <Button
                  onClick={() => (addDeletedUser(editingUser!))}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                onClick={() => (isEditingUser ? updateUser(editingUser!) : addCreatedUser(userForm))}
                disabled={!isUserFormValid()}
              >
                {isEditingUser ? t('common.update') : t('common.save')}
              </Button>
            </Group>

            {errorMessage && (
              <Group position="center" mt="md" color="red">
                <Text className="error-message" color="red">
                  {errorMessage !== '' ? errorMessage : null}
                </Text>
              </Group>
            )}
          </Tabs.Panel>
        </Tabs>
      </Modal>





    </>
  );
}
